import { useState, useEffect, useRef } from 'react';
import styles from '../styles/PlantSuggestions.module.css';
import PlantCard from '../components/PlantCard';
import PlantDetailPopup from '../components/PlantDetailPopup';
import gardenService from '../services/gardenService';
import plantDataService from '../services/plantDataService';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sun, Filter, Leaf } from 'lucide-react';

const API_BASE_URL = 'https://your-garden-guru-backend.vercel.app/api';

const PlantSuggestions = () => {
  const [location, setLocation] = useState('');
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [error, setError] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [plantData, setPlantData] = useState([]);
  const [filteredPlantData, setFilteredPlantData] = useState([]);
  const [locationCoordinates, setLocationCoordinates] = useState(null);
  const [locationDisplayName, setLocationDisplayName] = useState('');
  //const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [averageTemp, setAverageTemp] = useState(null);
  const [daysToHarvest, setDaysToHarvest] = useState('');
  const [growthDifficulty, setGrowthDifficulty] = useState("all");
  const [plantType, setPlantType] = useState("all");
  const [sortingType, setSortingType] = useState("alphabetical");
  const navigate = useNavigate();
  const galleryHeaderRef = useRef(null);


  // Get current location and load plants when component mounts
  useEffect(() => {
    // getCurrentLocation();
    loadPlantData();
  }, []);

  useEffect(() => {
    if (
      plantData.length > 0 &&
      (daysToHarvest === '' || (daysToHarvest >= 21 && daysToHarvest <= 540))
    ) {
      const timeout = setTimeout(() => {
        // console.log('plantatata', plantData);
        if (location) fetchWeatherData();
        else filterPlantsByCare(plantData);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [plantData, daysToHarvest]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (location.length > 3 && location.length < 5) fetchWeatherData();
      else filterPlantsByCare(plantData);
    }, 200);
    return () => clearTimeout(timeout);
  }, [location]);

  useEffect(() => {
    if (plantData.length > 0) {
      const timeout = setTimeout(() => {
        // console.log('growth filtered');
        if (location) fetchWeatherData();
        else filterPlantsByCare(plantData);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [growthDifficulty]);

  useEffect(() => {
    if (plantData.length > 0) {
      const timeout = setTimeout(() => {
        // console.log('plantType filtered');
        if (location) fetchWeatherData();
        else filterPlantsByCare(plantData);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [plantType]);

  // Function to load plant data using the shared service
  const loadPlantData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get plant data from the shared service
      const plants = await plantDataService.getPlantData();

      setPlantData(plants);
      setFilteredPlantData(plants);
      sortPlants(plants, "alphabetical");
    } catch (error) {
      // console.error('Error loading plant data:', error);
      setError('Failed to load plant data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };


  // Fetch weather data based on location name
  const fetchWeatherData = async () => {
    if (!location) return;

    setIsLoading(true);
    setError(null);
    // setUsingCurrentLocation(false);

    try {
      const response = await axios.get(`${API_BASE_URL}/location/weather`, {
        params: {
          postcode: location,
          days: 14,
        },
      });

      const parts = response.data.location.split(",");

      console.log("weather data", response.data);

      setWeatherData(response.data);
      setLocationCoordinates(response.data.coordinates);
      setLocationDisplayName(parts.slice(1, 3).join(", "));

      // console.log('Weather data:', response.data);

      // Filter plants based on the retrieved weather data
      const weatherfiltered = filterPlantsByWeather(response.data);

      // Filter plants based on selected criteria
      filterPlantsByCare(weatherfiltered);

    } catch (error) {
      // console.error('Error fetching weather data:', error);
      setError(error.response?.data?.message || 'Failed to fetch weather data. Please check your location and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter plants based on weather data
  const filterPlantsByWeather = (weatherData) => {
    const avgTemp = calculateAverageTemp(weatherData.weather.forecast.forecastday);

    // const condition = weatherData.weather.current?.condition?.text ||
    //   weatherData.weather.forecast?.forecastday[0]?.day?.condition?.text;

    // Apply weather-based filtering
    const tempPlantData = plantData;
    const weatherFiltered = tempPlantData.filter(plant => {
      // Extract temperature range 
      const tempRange = [plant.min_temperature_c, plant.max_temperature_c];

      if (tempRange) {
        const minTemp = parseInt(tempRange[0]);
        const maxTemp = parseInt(tempRange[1]);

        // Check if current temperature is within the plant's preferred range
        if (avgTemp < minTemp || avgTemp > maxTemp) {
          return false;
        }
      }

      return true;
    });

    return weatherFiltered;
  };

  // Filter plants based on selected criteria
  const filterPlantsByCare = (preFiltered) => {
    const filterCopy = [...preFiltered];
    // console.log("preFiltered", preFiltered);
    // console.log("filterCopy", filterCopy);

    const filteredPlants = filterCopy.filter(plant => {
      // Filter by days to harvest
      const harvestDaysMatch = daysToHarvest === null || daysToHarvest === "" ||
        (plant.min_days_to_harvest <= daysToHarvest && daysToHarvest <= plant.max_days_to_harvest);

      // Filter by growth difficulty
      const growthDifficultyMatch = growthDifficulty === 'all' ||
        plant.growth_difficulty.toLowerCase() === growthDifficulty.toLocaleLowerCase();

      // Filter by plant type
      const plantCategoryMatch = plantType === 'all' ||
        plant.category.toLowerCase() === plantType.toLocaleLowerCase();

      if (harvestDaysMatch && growthDifficultyMatch && plantCategoryMatch) {
        return true;
      }
      else {
        return false;
      }
    });

    // console.log('filtered by care')
    // filterPlantsBySeason(filteredPlants)
    sortPlants(filteredPlants, sortingType)
  }

  // Sort plants
  const sortPlants = (plants, sorting) => {
    // console.log("we sorting");
    const sortingPlants = plants ? plants : [...filteredPlantData];
    setSortingType(sorting);
    if (sorting === "alphabetical") {
      // Sort plants by alphabetical order
      const sorted = sortingPlants.sort((plantA, plantB) =>
        plantA.common_name.localeCompare(plantB.common_name, undefined, { sensitivity: 'base' })
      );

      filterPlantsBySeason(sorted);
    }
    else {
      // Sort plants by days to harvest
      const sorted = sortingPlants.sort((plantA, plantB) =>
        plantA.min_days_to_harvest - plantB.min_days_to_harvest
      );

      filterPlantsBySeason(sorted);
    }
  };

  // function to get current season 
  const getCurrentSeason = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();

    if ((month === 3 && day >= 20) || (month > 3 && month < 6) || (month === 6 && day <= 20)) {
      return "Autumn";
    } else if ((month === 6 && day >= 21) || (month > 6 && month < 9) || (month === 9 && day <= 21)) {
      return "Winter";
    } else if ((month === 9 && day >= 22) || (month > 9 && month < 12) || (month === 12 && day <= 20)) {
      return "Spring";
    } else {
      return "Summer";
    }
  };

  // filter out plants by opposite season
  const filterPlantsBySeason = (plantArray) => {
    const currentSeason = getCurrentSeason();
    // console.log('pre filter', plantArray.length);

    const filtered = plantArray.filter(plant => {
      // fetch array of optimal season for each plant
      const seasons = plant.optimal_season.split(',').map(item => item.trim());
      // filter it out if it's currently winter and summer is an optimal season
      if (currentSeason === 'Winter' && seasons.includes('Summer')) return false;
      // and vice versa
      if (currentSeason === 'Summer' && seasons.includes('Winter')) return false;
      // otherwise dont filter it out
      return true;
    });

    // console.log('post filter', filtered.length);
    setFilteredPlantData(filtered);
  };

  const submitFilters = (e) => {
    // if location exists with regex 
    if (location.trim() && /^\d{4}$/.test(location)) {
      // if harvestdays is null OR exists with regex
      if (daysToHarvest == '' || daysToHarvest && /^(2[1-9]|[3-9][0-9]|[1-4][0-9]{2}|5[0-3][0-9]|540)$/.test(daysToHarvest)) {
        fetchWeatherData();
      }
    }
    //else location is null
    else {
      // if harvest days exists with regex
      if (daysToHarvest && /^(2[1-9]|[3-9][0-9]|[1-4][0-9]{2}|5[0-3][0-9]|540)$/.test(daysToHarvest)) {
        fetchWeatherData();
      }
    }
  };

  // Modified handleAddToGarden function for PlantSuggestions.js
  const handleAddToGarden = (plant) => {
    // Get random position for the plant
    const randomX = Math.floor(Math.random() * 300) + 50;
    const randomY = Math.floor(Math.random() * 200) + 50;

    // Create a new plant object with necessary properties
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    // Calculate expected date based on growing time (use days to maturity if available)
    // Default to 90 days if not specified
    const growingDays = plant.days_to_maturity || 90;
    const expectedDate = new Date(today);
    expectedDate.setDate(today.getDate() + growingDays);
    const formattedExpectedDate = expectedDate.toISOString().split('T')[0];

    const newPlant = {
      id: Date.now(),
      type: plant.common_name.toLowerCase(),
      x: randomX,
      y: randomY,
      status: 'healthy',
      progress: 0,
      nextTask: 'water',
      waterLevel: 100,
      health: 100,
      startDate: formattedDate,
      stage: 'seedling',
      expectedDate: formattedExpectedDate,
      // Simply include the complete plant data
      plantData: plant,
      // Use imageUrl to match what PixelGarden expects
      imageUrl: plant.representative_image_url,
      icon_image_url: plant.icon_image_url
    };

    // Add plant to garden service
    gardenService.addPlant(newPlant);

    // Show confirmation message
    if (confirm(`${plant.common_name} has been added to your garden!\n\nClick OK to go to your virtual garden. \nClick Cancel to keep browsing plants.`)) {
      navigate("/dashboard");
    }
  };

  // Open plant detail popup
  const openPlantDetail = (plant) => {
    setSelectedPlant(plant);
    setShowPopup(true);
  };

  // Close plant detail popup
  const closePlantDetail = () => {
    setShowPopup(false);
  };

  // Calculate average temp of 14 day forecast
  const calculateAverageTemp = (forecast) => {
    // console.log('calculating average temp');
    let forecastAvgSum = 0;
    forecast.forEach(day => {
      forecastAvgSum += day.day.avgtemp_c;
    });

    let avgTemp = Math.round(forecastAvgSum / forecast.length);

    setAverageTemp(avgTemp);
    return avgTemp;
  };

  // Subscribe to plant data changes from shared service
  useEffect(() => {
    const unsubscribe = plantDataService.subscribe((updatedPlants) => {
      setPlantData(updatedPlants);
      setFilteredPlantData(updatedPlants);
      sortPlants(updatedPlants, sortingType);
    });

    return unsubscribe;
  }, [sortingType]);

  return (
    <div className={styles.layoutContainer}>
      {/* "Easel" Filter Panel */}
      <aside className={styles.sidebarPanel}>
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>
            <Filter size={18} /> Filters
          </h2>
          <button
            className={styles.btnReset}
            onClick={() => {
              setLocation('');
              setDaysToHarvest('');
              setGrowthDifficulty('all');
              setPlantType('all');
              setWeatherData(null);
              window.scrollTo({ top: 0 });
            }}
          >
            Reset
          </button>
        </div>

        <div className={styles.controlGroup}>
          <label className={styles.controlLabel}>Your Location</label>
          <input
            type="text"
            className={styles.inputField}
            value={location}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d{0,4}$/.test(val)) setLocation(val);
            }}
            onKeyDown={(e) => e.key === "Enter" && submitFilters()}
            placeholder="Enter Postcode..."
            maxLength={4}
            required
          />
          {!location && (
            <small style={{ color: '#A3392C', marginTop: '5px', display: 'block' }}>
              Please enter a valid Australian postcode
            </small>
          )}
          {location && location.length < 4 && (
            <small style={{ color: '#A3392C', marginTop: '5px', display: 'block' }}>
              Postcode must be 4 digits
            </small>
          )}
        </div>

        {weatherData && (
          <div className={styles.weatherStamp}>
            <div className={styles.weatherHeader}>
              {/* <Sun size={18} /> */}
              <img src={weatherData.weather.current?.condition?.icon} alt="Weather Icon" style={{ width: '40px', height: '40px' }} />
              <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{locationDisplayName}</span>
            </div>
            <div className={styles.weatherDetail} style={{ flex: '1' }}>
              <p><strong>14 Day Average Temp: </strong>
                {averageTemp
                  ? `${averageTemp}°C`
                  : 'Not available'
                }</p>
              <p><strong>Condition: </strong> {
                weatherData.weather.current?.condition?.text ||
                weatherData.weather.forecast?.forecastday[0]?.day?.condition?.text ||
                'Not available'
              }</p>
              <p><strong>Humidity:</strong> {
                weatherData.weather.current?.humidity !== undefined
                  ? `${weatherData.weather.current.humidity}%`
                  : weatherData.weather.forecast?.forecastday[0]?.day?.avghumidity !== undefined
                    ? `${weatherData.weather.forecast.forecastday[0].day.avghumidity}%`
                    : 'Not available'
              }</p>
            </div>
          </div>
        )}

        <div className={styles.controlGroup}>
          <label className={styles.controlLabel}>Plant Variety</label>
          <div className={styles.selectWrapper}>
            <select
              className={styles.inputField}
              value={plantType}
              onChange={(e) => setPlantType(e.target.value)}
            >
              <option value="all">All Varieties</option>
              <option value="flower">Flower</option>
              <option value="fruit">Fruit</option>
              <option value="grain">Grain</option>
              <option value="herb">Herb</option>
              <option value="legume">Legume</option>
              <option value="vegetable">Vegetable</option>
            </select>
          </div>
        </div>

        <div className={styles.controlGroup}>
          <label className={styles.controlLabel}>Difficulty Level</label>
          <div className={styles.selectWrapper}>
            <select
              className={styles.inputField}
              value={growthDifficulty}
              onChange={(e) => setGrowthDifficulty(e.target.value)}
            >
              <option value="all">Any Level</option>
              <option value="easy">Beginner Friendly</option>
              <option value="medium">Intermediate</option>
              <option value="hard">Expert</option>
            </select>
          </div>
        </div>

        <div className={styles.controlGroup}>
          <label className={styles.controlLabel}>Days to Harvest</label>
          
          <input
            type="text"
            className={styles.inputField}
            value={daysToHarvest}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d{0,3}$/.test(val)) setDaysToHarvest(val);
            }}
            min="21"
            max="540"
            placeholder="Max days (e.g. 60)"
          />
          {daysToHarvest && (daysToHarvest < 21 || daysToHarvest > 540) && (
            <small style={{ color: '#A3392C', marginTop: '5px', display: 'block' }}>
              Days must be between 21 and 540
            </small>
          )}
        </div>
      </aside>

      {/* Main Gallery Area */}
      <main className={styles.gallerySection}>
        <div ref={galleryHeaderRef} className={styles.galleryHeader}>
          <div className={styles.galleryHeaderTop}>
            <div className={styles.galleryHeaderContent}>
              <h1 className={styles.mainHeading}>Garden Journal</h1>
              <p className={styles.subHeading}>
                Curated selections for a peaceful harvest.
              </p>
            </div>

            <div className={styles.galleryHeaderActions}>
              <div className={styles.selectWrapper}>
                <select
                  className={styles.inputField}
                  value={sortingType}
                  onChange={(e) => sortPlants(filteredPlantData, e.target.value)}
                >
                  <option value="alphabetical">A-Z</option>
                  <option value="daysToHarvest">Fastest Harvest</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {isLoadingLocation && (
          <div className={styles.loadingState}>
            <Leaf size={40} className={styles.spinner} style={{ animation: 'spin 2s linear infinite' }} />
            <p>Observing the weather patterns...</p>
          </div>
        )}

        {isLoading && !isLoadingLocation ? (
          <div className={styles.loadingState}>
            <Leaf size={40} className={styles.spinner} style={{ animation: 'spin 2s linear infinite' }} />
            <p>Gathering your collection...</p>
          </div>
        ) : (
          <div className={styles.plantGrid}>
            {filteredPlantData.map((plant, index) => (
              <PlantCard
                key={plant.plant_id}
                index={index}
                plant={plant}
                onLearnMore={() => {
                  setSelectedPlant(plant);
                  openPlantDetail(plant);
                }}
                onAddToGarden={() => handleAddToGarden(plant)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Detail Popup */}
      {showPopup && selectedPlant && (
        <PlantDetailPopup
          plant={selectedPlant}
          onClose={() => closePlantDetail()}
          onAddToGarden={() => handleAddToGarden(selectedPlant)}
        />
      )}
    </div>
  );
};

export default PlantSuggestions;